/**
 * VoiceService — WebSocket + Web Audio API service for voice conversation.
 *
 * Handles:
 * - WebSocket connection to backend voice proxy
 * - Microphone capture (16kHz mono PCM via ScriptProcessorNode)
 * - Audio playback (queued AudioBufferSourceNode for gapless output)
 * - Audio level (RMS) for VU meter
 * - JSON control/event messaging
 */

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Derive WebSocket URL from HTTP base URL
function getWsUrl(sessionId) {
  const url = new URL(BASE_URL);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}/api/voice/sessions/${sessionId}/live`;
}

class VoiceService {
  constructor() {
    this.ws = null;
    this.audioContext = null;
    this.mediaStream = null;
    this.scriptProcessor = null;
    this.sourceNode = null;
    this.isRecording = false;
    this.isConnected = false;

    // Playback queue
    this._playbackQueue = [];
    this._isPlaying = false;
    this._playbackTime = 0;
    this._pendingTurnComplete = false;

    // Callbacks
    this.onReady = null;
    this.onInputTranscription = null;
    this.onOutputTranscription = null;
    this.onTurnComplete = null;
    this.onPlaybackComplete = null;
    this.onError = null;
    this.onClosed = null;
    this.onAudioLevel = null;
    this.onMessagesSaved = null;
  }

  /**
   * Connect to the voice WebSocket and initialize audio capture.
   * @param {number} sessionId - Chat session ID
   */
  async connect(sessionId) {
    if (this.isConnected) return;

    try {
      // Request microphone access with echo cancellation
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Create AudioContext at 16kHz for PCM capture
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });

      // Set up capture pipeline
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
      // Buffer size 4096 at 16kHz = ~256ms chunks
      this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.scriptProcessor.onaudioprocess = (e) => {
        if (!this.isRecording || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);

        // Calculate RMS audio level for VU meter
        if (this.onAudioLevel) {
          let sum = 0;
          for (let i = 0; i < inputData.length; i++) {
            sum += inputData[i] * inputData[i];
          }
          const rms = Math.sqrt(sum / inputData.length);
          this.onAudioLevel(Math.min(1, rms * 5)); // Scale for visibility
        }

        // Convert Float32 to Int16 PCM
        const pcm16 = float32ToInt16(inputData);
        this.ws.send(pcm16.buffer);
      };

      // Connect nodes (source -> processor -> destination for audio to flow)
      this.sourceNode.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      // Open WebSocket
      const wsUrl = getWsUrl(sessionId);
      this.ws = new WebSocket(wsUrl);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this.isConnected = true;
      };

      this.ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          // Binary PCM audio from Gemini — queue for playback
          this._enqueueAudio(event.data);
        } else {
          // JSON event
          try {
            const msg = JSON.parse(event.data);
            this._handleMessage(msg);
          } catch (e) {
            console.error('Invalid JSON from server:', e);
          }
        }
      };

      this.ws.onerror = (event) => {
        console.error('Voice WebSocket error:', event);
        if (this.onError) this.onError('Connection error');
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        if (this.onClosed) this.onClosed();
      };
    } catch (err) {
      console.error('Failed to initialize voice:', err);
      this.disconnect();
      if (err.name === 'NotAllowedError') {
        if (this.onError) this.onError('Microphone permission denied');
      } else {
        if (this.onError) this.onError('Failed to start voice: ' + err.message);
      }
      throw err;
    }
  }

  /**
   * Handle incoming JSON messages from the server.
   */
  _handleMessage(msg) {
    switch (msg.type) {
      case 'ready':
        if (this.onReady) this.onReady();
        break;
      case 'input_transcription':
        if (this.onInputTranscription) this.onInputTranscription(msg.text);
        break;
      case 'output_transcription':
        if (this.onOutputTranscription) this.onOutputTranscription(msg.text);
        break;
      case 'turn_complete':
        if (this.onTurnComplete) this.onTurnComplete();
        break;
      case 'messages_saved':
        if (this.onMessagesSaved) this.onMessagesSaved(msg.messages);
        break;
      case 'error':
        if (this.onError) this.onError(msg.message);
        break;
      default:
        console.warn('Unknown voice message type:', msg.type);
    }
  }

  /**
   * Start push-to-talk recording — send activity_start signal.
   */
  startRecording() {
    if (!this.isConnected || this.isRecording) return;
    this.isRecording = true;

    // Stop any playing audio
    this._stopPlayback();

    // Send activity start signal
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'start' }));
    }
  }

  /**
   * Stop push-to-talk recording — send activity_end signal.
   */
  stopRecording() {
    if (!this.isRecording) return;
    this.isRecording = false;

    // Reset audio level
    if (this.onAudioLevel) this.onAudioLevel(0);

    // Send activity end signal
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop' }));
    }
  }

  /**
   * Enqueue PCM audio data for gapless playback.
   * Schedules each chunk immediately on arrival for seamless playback
   * (no waiting for onended callbacks which cause micro-gaps/clicks).
   */
  _enqueueAudio(pcmData) {
    // Create playback context at browser's native rate (not forced 24kHz)
    if (!this._playbackContext) {
      this._playbackContext = new (window.AudioContext || window.webkitAudioContext)();
      this._playbackTime = this._playbackContext.currentTime;
      this._activeSources = [];
    }

    // Convert Int16 PCM to Float32
    const int16 = new Int16Array(pcmData);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768;
    }

    // Apply short fade-in/fade-out to prevent clicks at chunk boundaries
    const fadeLen = Math.min(64, Math.floor(float32.length / 2));
    for (let i = 0; i < fadeLen; i++) {
      const gain = i / fadeLen;
      float32[i] *= gain;
      float32[float32.length - 1 - i] *= gain;
    }

    // Create AudioBuffer at 24kHz source rate (browser resamples to native rate)
    const audioBuffer = this._playbackContext.createBuffer(1, float32.length, 24000);
    audioBuffer.getChannelData(0).set(float32);

    const source = this._playbackContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this._playbackContext.destination);

    // Schedule for gapless playback — immediate, no queue delay
    const now = this._playbackContext.currentTime;
    const startTime = Math.max(now, this._playbackTime);
    source.start(startTime);
    this._playbackTime = startTime + audioBuffer.duration;

    // Track active sources for stop and playback completion
    this._activeSources.push(source);
    source.onended = () => {
      const idx = this._activeSources.indexOf(source);
      if (idx > -1) this._activeSources.splice(idx, 1);
      // When all audio finishes and turn was marked complete, fire playback complete
      if (this._activeSources.length === 0 && this._pendingTurnComplete) {
        this._pendingTurnComplete = false;
        if (this.onPlaybackComplete) this.onPlaybackComplete();
      }
    };
  }

  /**
   * Mark that the backend turn is complete.
   * If audio is still playing, defer the playback-complete callback until audio ends.
   * If no audio is playing, fire immediately.
   */
  markTurnComplete() {
    if (this._activeSources && this._activeSources.length > 0) {
      this._pendingTurnComplete = true;
    } else {
      this._pendingTurnComplete = false;
      if (this.onPlaybackComplete) this.onPlaybackComplete();
    }
  }

  /**
   * Stop all currently queued/playing audio.
   */
  _stopPlayback() {
    this._playbackQueue = [];
    this._isPlaying = false;
    this._pendingTurnComplete = false;
    if (this._activeSources) {
      this._activeSources.forEach((s) => { try { s.stop(); } catch (e) { /* already ended */ } });
      this._activeSources = [];
    }
    if (this._playbackContext) {
      this._playbackTime = this._playbackContext.currentTime;
    }
  }

  /**
   * Disconnect and clean up all resources.
   */
  disconnect() {
    this.isRecording = false;
    this.isConnected = false;

    // Send close signal
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'close' }));
      } catch (e) { /* ignore */ }
    }

    // Close WebSocket
    if (this.ws) {
      this.ws.onclose = null; // prevent callback
      this.ws.close();
      this.ws = null;
    }

    // Stop audio capture
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    // Stop playback
    this._stopPlayback();
    if (this._playbackContext) {
      this._playbackContext.close().catch(() => {});
      this._playbackContext = null;
    }
  }
}

/**
 * Convert Float32 audio samples to Int16 PCM (little-endian).
 */
function float32ToInt16(float32Array) {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
}

export default VoiceService;
